import { defineTemplate, field } from '../egress/data-classes.js';
import { sqlIn } from '../utils/sql-in.js';
import { jsonText } from '../utils/sql-dialect.js';
import { composeControlEmbedText, matchControlsToText } from './register-vector-service.js';
import { billIds, findCoveringRulesForDraft } from './jurisdiction-rule-service.js';
import { statuteTextSha256 } from '../adapters/statute-text-store.js';
import { dutyActorOf } from '../data/intake-enums.js';
import { ensureStatuteObligationSet } from './statute-obligation-set-service.js';
export const DEFAULT_CONTROL_IMPACT_SETTINGS = {
    version: 'v112.1-code-default', top_k: 5, match_min_score: 0.6, rerank: true, duty_cap: 8,
    secondary_source_max_confidence: 0.7, low_confidence_below: 0.5, extraction: 'legacy',
};
const clamp01 = (v, d) => (typeof v === 'number' && v >= 0 && v <= 1 ? v : d);
const posInt = (v, d) => (Number.isInteger(v) && v > 0 ? v : d);
export async function getControlImpactSettings(db) {
    try {
        const row = (await db.query(`SELECT version, settings_json FROM control_impact_settings ORDER BY created_at DESC, version DESC LIMIT 1`)).results[0];
        if (!row)
            return DEFAULT_CONTROL_IMPACT_SETTINGS;
        const p = JSON.parse(row.settings_json);
        const d = DEFAULT_CONTROL_IMPACT_SETTINGS;
        return {
            version: row.version,
            top_k: posInt(p.top_k, d.top_k),
            match_min_score: clamp01(p.match_min_score, d.match_min_score),
            rerank: p.rerank !== false,
            duty_cap: posInt(p.duty_cap, d.duty_cap),
            secondary_source_max_confidence: clamp01(p.secondary_source_max_confidence, d.secondary_source_max_confidence),
            low_confidence_below: clamp01(p.low_confidence_below, d.low_confidence_below),
            extraction: p.extraction === 'shared' ? 'shared' : 'legacy',
        };
    }
    catch {
        return DEFAULT_CONTROL_IMPACT_SETTINGS; // before migration 765
    }
}
export const pendingControlImpact = (now, settingsVersion = DEFAULT_CONTROL_IMPACT_SETTINGS.version) => ({
    status: 'pending', suggested: null, reason: null, duties: [], duties_over_cap: 0, already_covered_by: [], flags: [],
    method: 'rerank', settings_version: settingsVersion, analyzed_at: now, error: null,
});
export const failedControlImpact = (now, error, settingsVersion = DEFAULT_CONTROL_IMPACT_SETTINGS.version) => ({
    ...pendingControlImpact(now, settingsVersion), status: 'failed', error,
});
// ── Egress templates (ADR-0073: platform scope, reference fields only) ───────
export const DUTY_EXTRACT_INSTRUCTIONS = [
    'You read the text of a law, regulation or court decision about AI.',
    'Return `duties`: each obligation the text places on someone. For each duty return',
    '`text` (one plain sentence), `actor` (deployer, developer, provider or other: who must do it;',
    'deployer = an organisation that uses an AI system, developer/provider = one that builds or sells it),',
    '`action`, `object`, `source_passage` (a short quote or section reference from the input),',
    '`confidence` in [0, 1], and `threshold`: null, or one of',
    '{"kind":"review_every_days","value":N} when the duty must be repeated at least every N days',
    '(or {"kind":"review_every_hours","value":N} when the interval is under a day),',
    '{"kind":"metric_min","metric":"<name>","value":N} or {"kind":"metric_max","metric":"<name>","value":N}',
    'when the duty sets a numeric floor or ceiling. Never invent duties the text does not state.',
].join(' ');
export const DUTY_RERANK_INSTRUCTIONS = [
    'You match one legal duty to the register control that implements it.',
    'The input JSON carries `duty` (text) and `candidates` (id + control text).',
    'Return `match_id` as the id of the candidate control whose purpose is to meet this duty,',
    'or null when no candidate does. Return `confidence` in [0, 1].',
    'Pick only from the listed candidate ids; never invent one.',
].join(' ');
const EXTRACT_TEMPLATE = defineTemplate('research.duty_extract', 'v1', DUTY_EXTRACT_INSTRUCTIONS, []);
const RERANK_TEMPLATE = defineTemplate('research.duty_control_rerank', 'v1', DUTY_RERANK_INSTRUCTIONS, []);
// ── Helpers ──────────────────────────────────────────────────────────────────
const PRIMARY_HOST = /(^|\.)(gov|gov\.uk|legislation\.gov\.uk|europa\.eu|eur-lex\.europa\.eu|legislature\.[a-z.]+|leginfo\.legislature\.ca\.gov|congress\.gov|govinfo\.gov|federalregister\.gov|courtlistener\.com|supremecourt\.gov|uscourts\.gov)$/i;
/** A URL whose host is a legislature, gazette or court (the primary text), not an aggregator. */
export function isPrimaryStatuteUrl(url) {
    if (!url)
        return false;
    try {
        const host = new URL(url).hostname;
        return PRIMARY_HOST.test(host) || /\.gov\.[a-z]{2}$/.test(host) || /(^|\.)(legislature|legis|senate|assembly|house)\./.test(host);
    }
    catch {
        return false;
    }
}
/** A state-numbered bill (AB / SB / HB / A. / S.) filed at country level ("US") points to a state law. */
export function jurisdictionLooksWrong(jurisdiction, title, billId) {
    if (!jurisdiction || jurisdiction.includes('-'))
        return false; // a sub-jurisdiction is already specific
    if (jurisdiction.toUpperCase() !== 'US')
        return false;
    const ids = [...billIds(`${title} ${billId ?? ''}`)];
    return ids.some((id) => /^(AB|SB|HB|A|S)\d/.test(id)) && !/\bH\.?R\.?\s?\d|\bS\.?\s?\d{1,4}\b.*Congress/i.test(title);
}
const ACTORS = ['deployer', 'developer', 'provider', 'other'];
const isDeployerSide = (a) => a === 'deployer' || a === 'developer' || a === 'provider';
function parseThreshold(t) {
    if (!t || typeof t !== 'object')
        return null;
    const o = t;
    const n = typeof o.value === 'number' && Number.isFinite(o.value) ? o.value : null;
    if (n === null)
        return null;
    if (o.kind === 'review_every_days' && n > 0)
        return n < 1 ? { kind: 'review_every_hours', value: Math.max(1, Math.round(n * 24)) } : { kind: 'review_every_days', value: Math.round(n) };
    if (o.kind === 'review_every_hours' && n > 0)
        return n >= 24 && n % 24 === 0 ? { kind: 'review_every_days', value: n / 24 } : { kind: 'review_every_hours', value: Math.max(1, Math.round(n)) };
    if ((o.kind === 'metric_min' || o.kind === 'metric_max') && typeof o.metric === 'string' && o.metric.trim()) {
        return { kind: o.kind, metric: o.metric.trim().toLowerCase().replace(/\s+/g, '_'), value: n };
    }
    return null;
}
/**
 * ADR-0117 §5: the duties of an obligation set, as this service's duty shape. Only obligations and prohibitions
 * bind; `platform` and `public_body` read as `other`; the first typed threshold is the one compared with a spec.
 */
export function dutiesFromObligationSet(set) {
    return set.obligations
        .filter((o) => o.deontic === 'obligation' || o.deontic === 'prohibition')
        .map((o) => ({
        text: o.obligation.slice(0, 400),
        actor: dutyActorOf(o.binds_whom.actor_class),
        threshold: o.law_parameters.thresholds[0] ?? null,
        source_passage: o.excerpt.slice(0, 500),
        confidence: o.confidence,
        action: '', object: '',
        proposed: o.control && 'new' in o.control ? o.control.new : null,
    }));
}
/**
 * The `duties` array of an extraction reply, or null when the reply has none. JSON-mode models sometimes return the
 * array JSON-encoded as a string (`"duties": "[...]"`); that is read as the array.
 */
export function dutiesList(out) {
    const v = out && typeof out === 'object' ? out.duties : undefined;
    if (Array.isArray(v))
        return v;
    if (typeof v === 'string') {
        try {
            const p = JSON.parse(v);
            return Array.isArray(p) ? p : null;
        }
        catch {
            return null;
        }
    }
    return null;
}
function parseDuties(out) {
    const list = dutiesList(out) ?? [];
    const duties = [];
    for (const d of list) {
        if (!d || typeof d !== 'object')
            continue;
        const o = d;
        const text = typeof o.text === 'string' ? o.text.trim() : '';
        if (!text)
            continue;
        const actor = ACTORS.includes(o.actor) ? o.actor : 'other';
        duties.push({
            text: text.slice(0, 400), actor, threshold: parseThreshold(o.threshold),
            source_passage: typeof o.source_passage === 'string' && o.source_passage.trim() ? o.source_passage.trim().slice(0, 500) : null,
            confidence: clamp01(o.confidence, 0.5),
            action: typeof o.action === 'string' ? o.action.trim() : '', object: typeof o.object === 'string' ? o.object.trim() : '',
        });
    }
    return duties;
}
/**
 * CODE decides covered / amend for a matched control. The duty's typed threshold against the active spec:
 *   review_every_days N  → freshness_max_age_seconds must be set and ≤ N days, else amend it to N days;
 *   metric_min / max     → threshold_default for the same metric must be at least as strict, else amend it.
 * No typed threshold, or no active spec to compare with → covered (the control exists for the duty).
 */
export function compareWithSpec(threshold, spec) {
    if (!threshold)
        return { outcome: 'covered', spec_field: null, current_value: null, proposed_value: null, reason: 'A control exists for this duty and the duty sets no measurable threshold.' };
    if (!spec)
        return { outcome: 'covered', spec_field: null, current_value: null, proposed_value: null, reason: 'A control exists for this duty; it has no active spec to compare the threshold with.' };
    if (threshold.kind === 'review_every_days' || threshold.kind === 'review_every_hours') {
        const want = threshold.kind === 'review_every_days' ? threshold.value * 24 * 3600 : threshold.value * 3600;
        const have = spec.freshness_max_age_seconds;
        const words = intervalWords(want);
        if (have !== null && have <= want)
            return { outcome: 'covered', spec_field: null, current_value: have, proposed_value: null, reason: `The spec already requires evidence at least every ${intervalWords(have)}.` };
        return { outcome: 'amend', spec_field: 'freshness_max_age_seconds', current_value: have, proposed_value: want,
            reason: have === null ? `The duty must be repeated every ${words}; the spec sets no freshness limit.` : `The duty must be repeated every ${words}; the spec allows ${intervalWords(have)}.` };
    }
    let current = {};
    try {
        current = spec.threshold_default_json ? JSON.parse(spec.threshold_default_json) : {};
    }
    catch {
        current = {};
    }
    const sameMetric = typeof current.metric !== 'string' || current.metric === threshold.metric;
    const bound = threshold.kind === 'metric_min' ? 'min' : 'max';
    const have = typeof current[bound] === 'number' ? current[bound] : null;
    const strictEnough = sameMetric && have !== null && (bound === 'min' ? have >= threshold.value : have <= threshold.value);
    if (strictEnough)
        return { outcome: 'covered', spec_field: null, current_value: current, proposed_value: null, reason: `The spec's ${bound} for ${threshold.metric} already meets ${threshold.value}.` };
    const proposed = { ...current, metric: threshold.metric, [bound]: threshold.value };
    return { outcome: 'amend', spec_field: 'threshold_default', current_value: current, proposed_value: proposed,
        reason: have === null || !sameMetric ? `The duty sets a ${bound} of ${threshold.value} for ${threshold.metric}; the spec has none.` : `The duty sets a ${bound} of ${threshold.value} for ${threshold.metric}; the spec has ${have}.` };
}
/** Seconds as plain words: "3 hours", "1 day", "90 days". */
export function intervalWords(seconds) {
    if (seconds < 86_400) {
        const h = Math.max(1, Math.round(seconds / 3600));
        return `${h} hour${h === 1 ? '' : 's'}`;
    }
    const d = Math.round(seconds / 86_400);
    return `${d} day${d === 1 ? '' : 's'}`;
}
const titleCase = (s) => s.replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
function draftControl(d) {
    if (d.proposed)
        return { family: 'ogcf', domain: 'governance', title: d.proposed.title.slice(0, 120), description: d.proposed.definition, framework_refs: [] };
    const title = titleCase([d.action, d.object].filter(Boolean).join(' ') || d.text).slice(0, 120);
    return { family: 'ogcf', domain: 'governance', title, description: d.text, framework_refs: [] };
}
// ── The analysis ─────────────────────────────────────────────────────────────
export async function analyzeControlImpact(deps, input, settings) {
    const now = (deps.now ?? (() => new Date().toISOString()))();
    const flags = [];
    if (jurisdictionLooksWrong(input.jurisdiction, input.title, input.bill_id))
        flags.push('jurisdiction_mismatch');
    const base = { status: 'ready', duties_over_cap: 0, flags, method: settings.rerank ? 'rerank' : 'embedding', settings_version: settings.version, analyzed_at: now, error: null };
    // 1. Already covered: the variant / duplicate verdict, or a live rule for the same statute / bill.
    const coveredBy = [];
    const sim = input.similarity;
    if (sim && (sim.decision === 'variant' || sim.decision === 'duplicate') && sim.matched_target_id)
        coveredBy.push(sim.matched_title ?? sim.matched_target_id);
    if (input.jurisdiction) {
        try {
            const rules = await findCoveringRulesForDraft(deps.db, { jurisdiction: input.jurisdiction, statute: input.title, citation: input.bill_id ?? input.title });
            coveredBy.push(...rules.map((r) => `rule ${r}`));
        }
        catch { /* no rule table / guard unavailable: not covered */ }
    }
    if (coveredBy.length) {
        return { ...base, suggested: 'informational', reason: 'already_covered', duties: [], already_covered_by: [...new Set(coveredBy)] };
    }
    // D-BL45-3: read the primary text when its URL is known; otherwise the summary, with capped confidence.
    let text = input.text;
    let primary = false;
    if (isPrimaryStatuteUrl(input.source_url) && deps.fetchPrimaryText) {
        const fetched = await deps.fetchPrimaryText(input.source_url).catch(() => null);
        if (fetched && fetched.trim().length > 200) {
            text = `${input.title}\n\n${fetched}`;
            primary = true;
        }
    }
    if (!primary)
        flags.push('secondary_source');
    const cap = primary ? 1 : settings.secondary_source_max_confidence;
    // 2. Duties: from the statute's shared obligation set (ADR-0117), or this service's own call (legacy).
    const shared = settings.extraction === 'shared';
    // Shared mode stores the text it reads (ADR-0117 §4); legacy only records its hash, so the v118.1 deploy writes nothing new.
    const textSha = shared && deps.statuteStore ? await deps.statuteStore.put(text) : await statuteTextSha256(text);
    let raw;
    if (shared) {
        const { set } = await ensureStatuteObligationSet({ db: deps.db, llm: deps.llm }, {
            instrument_key: input.bill_id || input.title, title: input.title, jurisdiction: input.jurisdiction,
            text, text_sha256: textSha, source: primary ? 'primary' : 'secondary',
        });
        raw = dutiesFromObligationSet(set);
    }
    else {
        const ex = await deps.llm.run({
            task: 'research.duty_extract', template: EXTRACT_TEMPLATE,
            input: field('research.source_text', text.slice(0, 12_000)),
            // An example of the shape, with `duties` as an array: a string here was copied back as a string (V112-4-10).
            schemaHint: { duties: [{ text: 'one plain sentence', actor: 'deployer | developer | provider | other', action: 'verb', object: 'what it applies to', threshold: null, source_passage: 'quote or section', confidence: 0.8 }] },
        });
        if (ex.status !== 'ok')
            throw new Error(`duty extraction refused: ${ex.reason ?? 'gateway refused'}`);
        // A reply the provider could not read as JSON comes back as {} — that is a failed analysis, not a law with no duties.
        if (!dutiesList(ex.output))
            throw new Error('duty extraction returned no readable duties list');
        raw = parseDuties(ex.output);
    }
    const all = raw.map((d) => ({ ...d, confidence: Math.min(d.confidence, cap) }));
    const capped = all.slice(0, settings.duty_cap);
    const over = all.slice(settings.duty_cap);
    const overCap = over.length;
    const duties = [];
    let usedEmbeddingOnly = !settings.rerank;
    for (const d of capped) {
        const common = { text: d.text, actor: d.actor, threshold: d.threshold, source_passage: d.source_passage, confidence: d.confidence, low_confidence: d.confidence < settings.low_confidence_below };
        if (!isDeployerSide(d.actor)) {
            duties.push({ ...common, outcome: 'not_deployer', control_id: null, control_title: null, spec_field: null, proposed_value: null, current_value: null, draft_control: null, reason: 'The duty falls on someone other than an AI deployer, developer or provider.' });
            continue;
        }
        // 3. Duty → control.
        const match = await matchDutyToControl(deps, d.text, settings);
        if (match.method === 'embedding')
            usedEmbeddingOnly = true;
        if (!match.control_id) {
            duties.push({ ...common, outcome: 'new', control_id: null, control_title: null, spec_field: null, proposed_value: null, current_value: null, draft_control: draftControl(d), reason: match.reason });
            continue;
        }
        const spec = (await deps.db.query(`SELECT control_id, freshness_max_age_seconds, threshold_default_json FROM control_specs WHERE control_id = ? AND superseded_at IS NULL ORDER BY created_at DESC LIMIT 1`, [match.control_id])).results[0] ?? null;
        const cmp = compareWithSpec(d.threshold, spec);
        duties.push({ ...common, outcome: cmp.outcome, control_id: match.control_id, control_title: match.title, spec_field: cmp.spec_field, proposed_value: cmp.proposed_value, current_value: cmp.current_value, draft_control: draftControl(d), reason: cmp.reason });
    }
    // Over the cap: listed with the text and a drafted control, not matched (no further model calls).
    for (const d of over) {
        duties.push({ text: d.text, actor: d.actor, threshold: d.threshold, source_passage: d.source_passage, confidence: d.confidence, low_confidence: d.confidence < settings.low_confidence_below,
            outcome: 'unanalysed', control_id: null, control_title: null, spec_field: null, proposed_value: null, current_value: null,
            draft_control: isDeployerSide(d.actor) ? draftControl(d) : null, reason: `Found beyond the limit of ${settings.duty_cap} duties a finding; not matched to a control.` });
    }
    // 4. Roll-up (code decides).
    const deployerDuties = duties.filter((x) => x.outcome !== 'not_deployer' && x.outcome !== 'unanalysed');
    const suggested = deployerDuties.some((x) => x.outcome === 'new') ? 'new_control'
        : deployerDuties.some((x) => x.outcome === 'amend') ? 'spec_amendment' : 'informational';
    return {
        ...base, method: usedEmbeddingOnly ? 'embedding' : 'rerank', duties, duties_over_cap: overCap, already_covered_by: [],
        extraction: shared ? 'shared' : 'legacy', text_sha256: textSha,
        suggested, reason: deployerDuties.length === 0 ? 'no_deployer_duties' : null,
    };
}
async function matchDutyToControl(deps, dutyText, settings) {
    if (!deps.index)
        return { control_id: null, title: null, method: 'embedding', reason: 'No register index to search; treated as a new control.' };
    const hits = (await matchControlsToText(deps.llm, deps.index, dutyText, settings.top_k)).filter((h) => h.score >= settings.match_min_score);
    if (hits.length === 0)
        return { control_id: null, title: null, method: settings.rerank ? 'rerank' : 'embedding', reason: 'No register control is close enough to this duty.' };
    const ids = hits.map((h) => h.control_id);
    const inIds = sqlIn(ids);
    const rows = (await deps.db.query(`SELECT control_id, title, description, family, domain, framework_refs FROM controls WHERE control_id IN ${inIds.sql}`, inIds.params)).results;
    const byId = new Map(rows.map((r) => [r.control_id, r]));
    const known = ids.filter((id) => byId.has(id));
    if (known.length === 0)
        return { control_id: null, title: null, method: 'embedding', reason: 'The closest index entries are no longer register controls.' };
    if (settings.rerank) {
        const payload = JSON.stringify({ duty: dutyText.slice(0, 600), candidates: known.map((id) => ({ id, text: composeControlEmbedText(byId.get(id)).slice(0, 600) })) });
        try {
            const r = await deps.llm.run({
                task: 'research.duty_control_rerank', template: RERANK_TEMPLATE, input: field('research.duty_match_payload', payload),
                schemaHint: { match_id: 'candidate id or null', confidence: '0..1' },
            });
            if (r.status === 'ok') {
                const id = r.output.match_id;
                if (id && known.includes(id))
                    return { control_id: id, title: byId.get(id).title, method: 'rerank', reason: '' };
                return { control_id: null, title: null, method: 'rerank', reason: 'The closest controls do something else; this duty needs a new control.' };
            }
        }
        catch { /* fall back to the embedding match below */ }
    }
    const top = known[0];
    return { control_id: top, title: byId.get(top).title, method: 'embedding', reason: '' };
}
// ── Shared entry for the runner, the backfill and Re-run (V112-4-4 / 4-5) ─────
/** The analysis input from a legal proposal's state (legislation: title / bill_id; case_law: case_name / summary). */
export function controlImpactInputFromProposal(targetType, state, similarity, fallbackText) {
    const s = (k) => (typeof state[k] === 'string' && state[k].trim() ? state[k].trim() : null);
    const title = (targetType === 'case_law' ? s('case_name') : s('title')) ?? s('title') ?? fallbackText?.split('\n')[0] ?? 'Untitled finding';
    const body = [s('description'), s('summary'), s('ai_relevance'), fallbackText].filter(Boolean).join('\n\n');
    return {
        title, text: `${title}\n\n${body}`.trim(), jurisdiction: s('jurisdiction'),
        bill_id: s('bill_id'), source_url: s('source_url'), similarity: similarity ?? null,
    };
}
/** The legal target types the analysis covers. */
export const controlImpactCovers = (targetType) => targetType === 'legislation' || targetType === 'case_law';
/** Run the analysis; never throws: a failure is stored as `status: 'failed'` with the error (no dead end: Re-run). */
export async function computeControlImpact(deps, input, settings) {
    const now = (deps.now ?? (() => new Date().toISOString()))();
    const s = settings ?? (await getControlImpactSettings(deps.db));
    try {
        return await analyzeControlImpact(deps, input, s);
    }
    catch (err) {
        return failedControlImpact(now, err instanceof Error ? err.message.slice(0, 300) : 'analysis failed', s.version);
    }
}
/**
 * V112-4-5: (re)analyse one pending legal proposal and store the result on it (Re-run, the admin backfill).
 * Returns null when the proposal isn't a pending legislation / case_law row.
 */
export async function refreshProposalControlImpact(deps, changeId, settings) {
    const row = (await deps.db.query(`SELECT target_type, status, proposed_state, impact FROM proposed_changes WHERE change_id = ?`, [changeId])).results[0];
    if (!row || row.status !== 'pending' || !controlImpactCovers(row.target_type))
        return null;
    const parse = (s) => { try {
        return s ? JSON.parse(s) : {};
    }
    catch {
        return {};
    } };
    const sim = parse(row.impact).similarity;
    const ci = await computeControlImpact(deps, controlImpactInputFromProposal(row.target_type, parse(row.proposed_state), sim ?? null), settings);
    const { ProposedChangeService } = await import('./proposed-change-service.js');
    if (!(await new ProposedChangeService(deps.db).setControlImpact(changeId, ci)))
        return null;
    // V116-2-4: a court decision's new analysis files the links it supports (only those not yet filed).
    if (row.target_type === 'case_law') {
        const { fileSuggestedCaseLinks } = await import('./case-link-service.js');
        await fileSuggestedCaseLinks(deps.db, changeId);
    }
    return ci;
}
/**
 * V112-4-5 (D-BL45-4: all pending): the next pending legal proposals without a ready analysis, after `cursor`.
 * `redoEmpty` also re-analyses ready rows that found no duties (no_deployer_duties) and that no editor has chosen on:
 * the rows an unreadable extraction reply stored as "no duties" before that was treated as a failure.
 */
export async function pendingLegalProposalsToAnalyse(db, cursor, limit, opts = {}) {
    const status = jsonText(db, 'impact', 'control_impact', 'status');
    const empty = opts.redoEmpty
        ? ` OR (${status} = 'ready' AND COALESCE(${jsonText(db, 'impact', 'control_impact', 'reason')}, '') = 'no_deployer_duties'
          AND ${jsonText(db, 'impact', 'control_impact', 'chosen')} IS NULL)`
        : '';
    return (await db.query(`SELECT change_id FROM proposed_changes
      WHERE status = 'pending' AND target_type IN ('legislation', 'case_law') AND change_id > ?
        AND (COALESCE(${status}, '') <> 'ready'${empty})
      ORDER BY change_id LIMIT ?`, [cursor, limit])).results.map((r) => r.change_id);
}
/** A jurisdiction code: country / bloc (US, EU, UK) or a sub-jurisdiction (US-CA, US-NY-NYC). */
export const JURISDICTION_CODE = /^[A-Z]{2,6}(-[A-Z0-9]{1,8}){0,2}$/;
/**
 * V112-4-7 (built-screen panel, jurisdiction_mismatch had no action): correct a pending law's jurisdiction before it is
 * approved. Only `jurisdiction` in proposed_state changes; who / from / to / when is kept on impact.jurisdiction_corrected.
 * Returns false when the proposal isn't a pending legislation / case_law row.
 */
export async function correctLegalJurisdiction(db, changeId, jurisdiction, by) {
    if (!JURISDICTION_CODE.test(jurisdiction))
        throw new Error(`Not a jurisdiction code: ${jurisdiction}`);
    const row = (await db.query(`SELECT target_type, status, proposed_state, impact FROM proposed_changes WHERE change_id = ?`, [changeId])).results[0];
    if (!row || row.status !== 'pending' || !controlImpactCovers(row.target_type))
        return false;
    const parse = (s) => { try {
        return s ? JSON.parse(s) : {};
    }
    catch {
        return {};
    } };
    const state = parse(row.proposed_state);
    const impact = parse(row.impact);
    const from = typeof state.jurisdiction === 'string' ? state.jurisdiction : null;
    const res = await db.execute(`UPDATE proposed_changes SET proposed_state = ?, impact = ? WHERE change_id = ? AND status = 'pending'`, [
        JSON.stringify({ ...state, jurisdiction }),
        JSON.stringify({ ...impact, jurisdiction_corrected: { from, to: jurisdiction, by, at: new Date().toISOString() } }),
        changeId,
    ]);
    return (res.meta?.changes ?? 0) > 0;
}
/**
 * V112-4-7 (panel r2, secondary_source had no action; D-BL45-3): add the statute's own link to a pending law so the
 * analysis reads the primary text. Only proposed_state.source_url changes (the previous link is kept on
 * impact.source_url_corrected with who / when). Returns false when the proposal isn't a pending legal row.
 */
export async function correctLegalSourceUrl(db, changeId, url, by) {
    let u;
    try {
        u = new URL(url);
    }
    catch {
        throw new Error('Not a link');
    }
    if (u.protocol !== 'https:')
        throw new Error('Use an https link');
    const row = (await db.query(`SELECT target_type, status, proposed_state, impact FROM proposed_changes WHERE change_id = ?`, [changeId])).results[0];
    if (!row || row.status !== 'pending' || !controlImpactCovers(row.target_type))
        return false;
    const parse = (s) => { try {
        return s ? JSON.parse(s) : {};
    }
    catch {
        return {};
    } };
    const state = parse(row.proposed_state);
    const impact = parse(row.impact);
    const from = typeof state.source_url === 'string' ? state.source_url : null;
    const res = await db.execute(`UPDATE proposed_changes SET proposed_state = ?, impact = ? WHERE change_id = ? AND status = 'pending'`, [
        JSON.stringify({ ...state, source_url: u.toString() }),
        JSON.stringify({ ...impact, source_url_corrected: { from, to: u.toString(), by, at: new Date().toISOString() } }),
        changeId,
    ]);
    return (res.meta?.changes ?? 0) > 0;
}
//# sourceMappingURL=control-impact-service.js.map